import { cloneDeep } from 'lodash-es'

import { UiMetadata } from '../../../models/formMetadata/FormMetadata'

/**
 * The subset of a content object these helpers touch. Deliberately structural rather than a
 * reference to `Content` or `NoteMetadata` — both are passed in by callers and their `meta_data`
 * types are not mutually assignable.
 */
export interface ContentWithFormMetadata {
  meta_data?: {
    schema?: { properties?: Record<string, unknown> }
    uiSchema?: UiMetadata
    initialValues?: Dict
  }
}

/**
 * Merges `content2`'s form metadata into `content1`'s and returns the result, leaving both inputs
 * untouched. Schema properties, `uiSchema` entries and `initialValues` from `content2` win on
 * collision; `ui:order` is concatenated with `content2`'s fields last, or first when `reverseOrder`
 * is set.
 *
 * Distinct from `extendMetadata` in `features/assignments/utils`, which takes a bare `FormMetadata`
 * rather than a content object wrapping one in `meta_data`.
 */
export const extendContentMetadata = <T extends ContentWithFormMetadata>(
  content1: T,
  content2: ContentWithFormMetadata,
  reverseOrder = false,
): T => {
  const content = cloneDeep(content1) as ContentWithFormMetadata
  const meta1 = content1.meta_data
  const meta2 = content2.meta_data
  const order1 = meta1?.uiSchema?.['ui:order'] ?? []
  const order2 = meta2?.uiSchema?.['ui:order'] ?? []

  content.meta_data = {
    ...content.meta_data,
    schema: { ...meta1?.schema, properties: { ...meta1?.schema?.properties, ...meta2?.schema?.properties } },
    uiSchema: {
      ...meta1?.uiSchema,
      ...meta2?.uiSchema,
      'ui:order': reverseOrder ? [...order2, ...order1] : [...order1, ...order2],
    },
    initialValues: { ...meta1?.initialValues, ...meta2?.initialValues },
  }

  return content as T
}

/**
 * Returns a copy of `content` with `fieldToRemove` dropped from its schema properties, its
 * `uiSchema` and its `ui:order`.
 */
export const removeContentMetadataField = <T extends ContentWithFormMetadata>(content: T, fieldToRemove: string): T => {
  const cloned = cloneDeep(content) as ContentWithFormMetadata
  const { schema, uiSchema } = cloned.meta_data ?? {}

  if (schema?.properties?.[fieldToRemove]) {
    delete schema.properties[fieldToRemove]
  }

  if (uiSchema) {
    const order = uiSchema['ui:order']
    if (Array.isArray(order)) {
      uiSchema['ui:order'] = order.filter((item) => item !== fieldToRemove)
    }
    if (uiSchema[fieldToRemove]) {
      delete uiSchema[fieldToRemove]
    }
  }

  return cloned as T
}
